import { Box, Card, CardContent, CardHeader, Typography } from "@mui/material";
import { useAuthMonitor } from "./api";
import Header from "@arelia-components/layout/Header";
import { ContentBox } from "@arelia-components/ui-atoms/ContentBox";
import SpinnerWrapper from "@arelia-components/ui-atoms/SpinnerWrapper";
import { ColumnDef, DataGrid } from "@arelia-components/ui-atoms/data-grid";
import { useMemo, useState } from "react";
import { countBy, keyBy } from "lodash";
import { formatRelativeTime } from "@arelia-utils/helpers/dates";
import { CardSearch } from "@arelia-components/ui-atoms/CardSearch";
import { useCustomersList } from "./hooks";
import { ProportionChart } from "./ProportionChart";
import { ArelTooltip } from "@arelia-components/ui-atoms/Tooltip";
import { orderBy } from "lodash";
import { MonitorRecord } from "./models";

export function AuthMonitor() {
  const { data, isLoading } = useAuthMonitor();
  const [searchValue, setSearchValue] = useState('');

  const records = useCustomersList(data || [], searchValue);
  const [rows, columns] = useMemo(() => {
    const appColumnNames: Record<string, string> = {};
    const rows = (records ?? []).map((x: MonitorRecord) => {
      const activeSession = x.authAppSessions.find(x => x.isConnected);
      const lastVersion = activeSession ?? orderBy(x.authAppSessions, x => x.connectedOn, "desc")[0];

      return {
        ...x,
        isConnected: activeSession?.isConnected ?? false,
        lastConnectedOn: activeSession?.connectedOn,
        downloads: countBy(x.authAppDownloads, 'platform'),
        version: lastVersion?.version,
        ...keyBy(x.apps, (y: any) => {
          const colName = `app_${y.name}`;
          appColumnNames[colName] = y.name;
          return colName;
        })
      };
    });

    return [orderBy(rows, ["isConnected", "lastConnectedOn"], ["desc"]), [
      {
        field: "fullName",
        headerName: "Customer Name",
        type: "text",
        renderer: ({ row }) => (
          <Box>
            <Typography variant="subtitle2">
              {row.fullName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.email}
            </Typography>
          </Box>
        )
      },
      { field: "bandName", headerName: "Band Name", type: "text", width: "20ch" },
      {
        field: "downloads", headerName: "Downloads", type: "text", renderer: ({ row }) =>
          Object.keys(row.downloads)
            .map((platform) => <Typography key={platform}>{platform}: {row.downloads[platform]}</Typography>)
      },
      {
        field: "isConnected", headerName: "Auth App", renderer: ({ row }) => {
          var content = row.isConnected ? (<Typography color="green">ACTIVE</Typography>) : (
            <Box>
              <Typography color="red">INACTIVE</Typography>
              {row.lastConnectedOn ? formatRelativeTime(row.lastConnectedOn) : ''}
            </Box>
          );

          var tooltip = row.authAppSessions.map((s: any) => `${formatRelativeTime(s.connectedOn)} - ${s.version}`).join("\n");

          return <ArelTooltip sx={{ whiteSpace: "pre-wrap" }} title={tooltip}>{content}</ArelTooltip>
        }
      },
      { field: "version", headerName: "Version", type: "text" },
      ...Object.entries(appColumnNames).map(([field, headerName]) => ({
        field,
        headerName,
        renderer: ({ value }: any) => <ArelTooltip title={tooltipElem(value.successCount, value.failureCount)}>
          <Box sx={styles.cellRenderer}>
            {value.lastUpdatedOn && <ProportionChart faillure={value.failureCount} success={value.successCount}></ProportionChart>}
            {value.lastUpdatedOn ? formatRelativeTime(value.lastUpdatedOn) : ''}
          </Box>
        </ArelTooltip>
      })),
      { field: "spacer", headerName: "" } //spacer
    ] as ColumnDef[]];
  }, [records]);

  const tooltipElem = (success: number, faillure: number) => {
    const total = success + faillure;
    const successPercent = total > 0 ? Math.round(success / total * 100) : 0;
    const faillurePercent = total > 0 ? Math.round(faillure / total * 100) : 0;
    return total > 0 ? <Box>
      <Typography>{success} Success - {successPercent}%</Typography>
      <Typography>{faillure} Faillure - {faillurePercent}%</Typography>
    </Box> : <Box>No activities</Box>;
  }

  return (
    <ContentBox>
      <Header title="Auth App Monitor" />
      <SpinnerWrapper loading={isLoading}>
        <Card sx={styles.card}>
          <CardHeader
            title="Customers"
            sx={styles.listCardHeader}
            action={<CardSearch value={searchValue} onChange={setSearchValue} />} />
          <CardContent>
            <DataGrid rows={rows} columns={columns} />
          </CardContent>
        </Card>
      </SpinnerWrapper>
    </ContentBox>
  );
}

const styles = {
  card: {
    overflow: "visible"
  },
  listCardHeader: {
    marginBottom: "32px"
  },
  cellRenderer: {
    display: "flex",
    flexDirection: "column",
    gap: "6px"
  }
}